import "@/index.css";
import "@/i18n";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { PrefsProvider } from "@/contexts/PrefsContext";
import { AccessProvider } from "@/contexts/AccessContext";
import { AppLockProvider } from "@/contexts/AppLockContext";
import AppLockScreen from "@/components/AppLockScreen";
import Layout from "@/components/Layout";
import Protected from "@/components/Protected";
import Paywall from "@/components/Paywall";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Forgot from "@/pages/Forgot";
import Reset from "@/pages/Reset";
import AuthCallback from "@/pages/AuthCallback";
import ChooseRole from "@/pages/ChooseRole";
import Dashboard from "@/pages/Dashboard";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import Classes from "@/pages/Classes";
import ClassDetail from "@/pages/ClassDetail";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import Personal from "@/pages/Personal";
import Todo from "@/pages/Todo";
import CalendarPage from "@/pages/Calendar";
import StudyHub from "@/pages/StudyHub";
import Billing from "@/pages/Billing";
import BillingSuccess from "@/pages/BillingSuccess";
import VerifyEmail from "@/pages/VerifyEmail";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Help from "@/pages/Help";
import Settings from "@/pages/Settings";
import Founder from "@/pages/Founder";
import AdminContent from "@/pages/AdminContent";
import SocialScheduler from "@/pages/SocialScheduler";
import CookieConsent from "@/components/CookieConsent";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import VersionChecker from "@/components/VersionChecker";
import { Toaster } from "@/components/ui/sonner";

function RoleGate({ children, allow }) {
    const { user, effectiveRole } = useAuth();
    // Founder "View As" swaps: honour the effective role so the founder
    // can browse role-restricted pages (e.g. /classes while viewing as
    // student) without touching their real account type.
    const role = effectiveRole || user?.role;
    if (user && role && allow && !allow.includes(role)) {
        return <Navigate to="/dashboard" replace />;
    }
    return children;
}

function HashGate({ children }) {
    const loc = useLocation();
    if (loc.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return children;
}

function App() {
    return (
        <AuthProvider>
            <PrefsProvider>
                <AccessProvider>
                <AppLockProvider>
                <BrowserRouter>
                <HashGate>
                    <AppLockScreen />
                    <Paywall />
                    <CookieConsent />
                    <ImpersonationBanner />
                    <VersionChecker />
                    <Toaster position="top-center" richColors closeButton />
                    <Routes>
                        <Route path="/" element={<Landing />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/signup" element={<Signup />} />
                        <Route path="/forgot" element={<Forgot />} />
                        <Route path="/reset-password" element={<Reset />} />
                        <Route path="/auth/callback" element={<AuthCallback />} />
                        <Route path="/choose-role" element={<ChooseRole />} />

                        <Route path="/billing/success" element={<BillingSuccess />} />
                        <Route path="/verify-email" element={<VerifyEmail />} />
                        <Route path="/privacy" element={<Privacy />} />
                        <Route path="/terms" element={<Terms />} />

                        <Route element={<Protected><Layout /></Protected>}>
                            <Route path="/dashboard" element={<Dashboard />} />
                            <Route path="/billing" element={<Billing />} />
                            <Route path="/clients" element={<RoleGate allow={["professional"]}><Clients /></RoleGate>} />
                            <Route path="/clients/:id" element={<RoleGate allow={["professional"]}><ClientDetail /></RoleGate>} />
                            <Route path="/classes" element={<RoleGate allow={["student"]}><Classes /></RoleGate>} />
                            <Route path="/classes/:id" element={<RoleGate allow={["student"]}><ClassDetail /></RoleGate>} />
                            <Route path="/projects" element={<Projects />} />
                            <Route path="/projects/:id" element={<ProjectDetail />} />
                            <Route path="/personal" element={<Personal />} />
                            <Route path="/todo" element={<Todo />} />
                            <Route path="/calendar" element={<CalendarPage />} />
                            <Route path="/study-hub" element={<RoleGate allow={["student"]}><StudyHub /></RoleGate>} />
                            <Route path="/social" element={<SocialScheduler />} />
                            <Route path="/settings" element={<Settings />} />
                            <Route path="/founder" element={<Founder />} />
                            <Route path="/admin/content" element={<AdminContent />} />
                            <Route path="/help" element={<Help />} />
                        </Route>

                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </HashGate>
                </BrowserRouter>
                </AppLockProvider>
                </AccessProvider>
            </PrefsProvider>
        </AuthProvider>
    );
}

export default App;
